
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const ProfilePasswordForm = () => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
  };

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast({
        variant: "destructive",
        title: "Passwords do not match",
      });
      return;
    }

    setSaving(true);

    // Get user id and email
    const { data: userResult } = await supabase.auth.getUser();
    const email = userResult?.user?.email;
    if (!email) {
      toast({
        variant: "destructive",
        title: "Could not get user email",
      });
      setSaving(false);
      return;
    }

    // Re-authenticate (sign in) with current password, then update password
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password: passwordForm.currentPassword,
    });
    if (signInError) {
      toast({
        variant: "destructive",
        title: "Current password incorrect",
      });
      setSaving(false);
      return;
    }

    const { error } = await supabase.auth.updateUser({
      password: passwordForm.newPassword,
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Failed to update password",
        description: error.message,
      });
    } else {
      toast({
        title: "Password updated",
        description: "Your password has been changed.",
      });
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    }
    setSaving(false);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Security</CardTitle>
        <CardDescription>Manage your account security</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={handleChangePassword}>
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Current Password</Label>
            <Input
              id="currentPassword"
              name="currentPassword"
              type="password"
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
              autoComplete="current-password"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              name="newPassword"
              type="password"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              autoComplete="new-password"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              autoComplete="new-password"
            />
          </div>
          <Button className="bg-ai-indigo hover:bg-ai-indigo/90" type="submit" disabled={saving}>
            {saving ? "Updating..." : "Update Password"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
